class BranchList extends HTMLElement {
  connectedCallback() {
    this.branches = Array.from(this.querySelectorAll('.boa-branch-item'));
    this.emptyState = this.querySelector('.boa-branches-list__empty-state');
    this.container = this.querySelector('[data-branches-container]');
    this.selectedVerifoneId = this.getAttribute('data-selected-verifone-id');
    
    // Default: keep the current UX (alphabetical sort).
    // If user allows geolocation, we will override the order + show distances.
    const sortContainer = this.container?.closest('[data-sort-branches]');
    if (sortContainer) {
      this.sortBranchesAlphabetically();
      this.sortBranchesByUserLocationIfPermitted();
    }
    
    if (this.selectedVerifoneId) {
      this.highlightSelectedBranch(this.selectedVerifoneId);
    }
  }

  sortBranchesAlphabetically() {
    if (!this.container || this.branches.length === 0) {
      return;
    }

    const sortedBranches = [...this.branches].sort((a, b) => {
      const titleA = (a.querySelector('.boa-branch-item__title')?.textContent || '').trim().toLowerCase();
      const titleB = (b.querySelector('.boa-branch-item__title')?.textContent || '').trim().toLowerCase();
      return titleA.localeCompare(titleB);
    });

    sortedBranches.forEach(branch => {
      this.container.appendChild(branch);
    });

    this.branches = sortedBranches;
  }

  sortBranchesByUserLocationIfPermitted() {
    if (this.geolocationSetupDone) {
      return;
    }
    this.geolocationSetupDone = true;

    if (!this.container || this.branches.length === 0) {
      return;
    }

    if (!navigator?.geolocation?.getCurrentPosition) {
      return;
    }

    this.watchGeolocationPermission();
    this.requestUserLocationAndSort();
  }

  watchGeolocationPermission() {
    if (!navigator.permissions?.query) {
      return;
    }

    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      this.geolocationPermissionStatus = status;

      if (status.state === 'denied') {
        return;
      }

      status.addEventListener('change', () => {
        if (status.state === 'granted' && !this.locationSortApplied) {
          if (this.geolocationFetchInProgress) {
            this.pendingRetryAfterPermissionGrant = true;
            return;
          }
          this.requestUserLocationAndSort();
        }
      });
    }).catch(() => {});
  }

  finishGeolocationFetch() {
    this.geolocationFetchInProgress = false;

    if (this.pendingRetryAfterPermissionGrant && !this.locationSortApplied) {
      this.pendingRetryAfterPermissionGrant = false;
      setTimeout(() => {
        this.requestUserLocationAndSort();
      }, 100);
    }
  }

  requestUserLocationAndSort() {
    if (this.locationSortApplied || this.geolocationFetchInProgress) {
      return;
    }

    this.geolocationFetchInProgress = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLatitude = position?.coords?.latitude;
        const userLongitude = position?.coords?.longitude;
        if (typeof userLatitude !== 'number' || typeof userLongitude !== 'number') {
          this.finishGeolocationFetch();
          this.scheduleLocationRetry();
          return;
        }

        this.finishGeolocationFetch();
        this.applyLocationSort(userLatitude, userLongitude);
      },
      (error) => {
        this.finishGeolocationFetch();

        // TIMEOUT is common while the permission dialog is open; retry after grant.
        if (error?.code === error.TIMEOUT) {
          this.scheduleLocationRetry();
          return;
        }

        if (this.geolocationPermissionStatus?.state === 'granted') {
          this.scheduleLocationRetry();
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 600000
      }
    );
  }

  scheduleLocationRetry() {
    if (this.locationSortApplied) {
      return;
    }

    this.geolocationRetryCount = (this.geolocationRetryCount || 0) + 1;
    if (this.geolocationRetryCount > 3) {
      return;
    }

    setTimeout(() => {
      this.requestUserLocationAndSort();
    }, this.geolocationRetryCount * 400);
  }

  applyLocationSort(userLatitude, userLongitude) {
    if (this.locationSortApplied) {
      return;
    }

    this.locationSortApplied = true;
    this.sortBranchesByCoordinates(userLatitude, userLongitude);
    this.dispatchEvent(new CustomEvent('branches:sorted-by-location', { bubbles: true }));
  }

  sortBranchesByCoordinates(userLat, userLng) {
    // Reorder only if at least one branch has lat/lng values.
    if (!this.container) {
      return;
    };
    const anyCoords = this.branches.some((branch) => {
      const lat = parseFloat(branch.getAttribute('data-branch-lat'));
      const lng = parseFloat(branch.getAttribute('data-branch-lng'));
      return Number.isFinite(lat) && Number.isFinite(lng);
    });

    if (!anyCoords) {
      return;
    }

    // Update distance labels first (so the text matches the branch cards we move around).
    this.updateBranchDistances(userLat, userLng);

    const sortedBranches = [...this.branches].sort((a, b) => {
      const distanceA = this.getBranchDistanceKm(userLat, userLng, a);
      const distanceB = this.getBranchDistanceKm(userLat, userLng, b);
      return distanceA - distanceB; // nearest -> farthest
    });

    sortedBranches.forEach((branch) => {
      this.container.appendChild(branch);
    });

    this.branches = sortedBranches;
  }

  updateBranchDistances(userLat, userLng) {
    // Fill the "X km" label next to icons for each branch card.
    for (const branch of this.branches) {
      const lat = parseFloat(branch.getAttribute('data-branch-lat'));
      const lng = parseFloat(branch.getAttribute('data-branch-lng'));
      const distanceElement = branch.querySelector('.boa-branch-item__distance');

      if (!distanceElement) {
        continue;
      }

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        distanceElement.textContent = '';
        distanceElement.setAttribute('hidden', '');
        distanceElement.setAttribute('aria-hidden', 'true');
        continue;
      } 

      const distanceKm = this.haversineDistanceKm(userLat, userLng, lat, lng);
      const displayDistanceKm = this.formatKm(distanceKm);
      distanceElement.textContent = displayDistanceKm ? `${displayDistanceKm} km` : '';
      distanceElement.removeAttribute('hidden');
      distanceElement.setAttribute('aria-hidden', 'false');
    }
  }

  formatKm(km) {
    if (!Number.isFinite(km) || km < 0) {
      return '';
    }

    if (km < 10) { // e.g. 2.3 km
      return Math.round(km * 10) / 10;
    }
    return Math.round(km);
  }

  getBranchDistanceKm(userLat, userLng, branchElement) {
    const branchLatitude = parseFloat(branchElement.getAttribute('data-branch-lat'));
    const branchLongitude = parseFloat(branchElement.getAttribute('data-branch-lng'));
    if (!Number.isFinite(branchLatitude) || !Number.isFinite(branchLongitude)) {
      return Number.POSITIVE_INFINITY;
    }

    // Distance in km (Haversine).
    return this.haversineDistanceKm(userLat, userLng, branchLatitude, branchLongitude);
  }

  haversineDistanceKm(userLatDeg, userLngDeg, branchLatDeg, branchLngDeg) {
    // Returns the great-circle distance between user and branch (in km).
    // (Uses the Haversine formula.)
    const degToRad = (deg) => (deg * Math.PI) / 180;
    const earthRadiusKm = 6371;

    // Convert degrees to radians:
    // phi = latitude, lambda = longitude
    const phi1 = degToRad(userLatDeg);
    const phi2 = degToRad(branchLatDeg);
    const deltaPhi = degToRad(branchLatDeg - userLatDeg);
    const deltaLambda = degToRad(branchLngDeg - userLngDeg);

    // Haversine "a" part:
    // a = sin²(deltaPhi/2) + cos(phi1)*cos(phi2)*sin²(deltaLambda/2)
    const sinDeltaPhiOver2 = Math.sin(deltaPhi / 2);
    const sinDeltaLambdaOver2 = Math.sin(deltaLambda / 2);
    const a =
      (sinDeltaPhiOver2 * sinDeltaPhiOver2) +
      (Math.cos(phi1) * Math.cos(phi2) * sinDeltaLambdaOver2 * sinDeltaLambdaOver2);

    // Haversine "c" part:
    // c = 2 * atan2( sqrt(a), sqrt(1-a) )
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return earthRadiusKm * c;
  }

  highlightSelectedBranch(verifoneId) {
    if (!verifoneId) {
      return;
    }

    for (const branch of this.branches) {
      const branchVerifoneId = branch.getAttribute('data-branch-verifone-id');
      if (branchVerifoneId === verifoneId) {
        branch.classList.add('boa-branch-item--selected');
        break;
      }
    }
  }

  filterBranches(searchTerm) {
    this.filterBranchesByTerm(searchTerm, this.branches, this.emptyState, this.container);
  }

  filterBranchesByTerm(term, branches, emptyState, container) {
    const searchTerm = term.toLowerCase().trim();
    let visibleCount = 0;

    for (const branch of branches) {
      const city = (branch.getAttribute('data-branch-city') || '').toLowerCase();
      const title = branch.querySelector('.boa-branch-item__title')?.textContent?.toLowerCase() || '';
      const matches = searchTerm === '' || city.includes(searchTerm) || title.includes(searchTerm);
      
      if (matches) {
        branch.style.display = '';
        visibleCount++;
      } else {
        branch.style.display = 'none';
      }
    }

    if (visibleCount === 0 && searchTerm !== '') {
      if (emptyState) {
        emptyState.removeAttribute('hidden');
      }
      if (container) {
        container.setAttribute('hidden', '');
      }
    } else {
      if (emptyState) {
        emptyState.setAttribute('hidden', '');
      }
      if (container) {
        container.removeAttribute('hidden');
      }
    }
  }
}

class BranchSearch extends HTMLElement {
  constructor() {
    super();
    this.mouseOverResults = false;
    this.allBranches = [];
  }

  connectedCallback() {
    this.input = this.querySelector('.boa-branch-search__input');
    this.resultsContainer = this.querySelector('.boa-branch-search__results');
    this.resultsList = this.querySelector('.boa-branch-search__results-list');
    this.branchList = document.querySelector('branch-list');
    this.sectionContainer = this.closest('.shopify-section--boa-main-branches') || this.closest('.boa-branches-page') || document.body;
    
    if (!this.input) {
      return;
    }
    
    setTimeout(() => {
      this.collectBranches();
      if (!this.allBranches.length) {
        setTimeout(() => this.collectBranches(), 400);
      }
    }, 100);
    
    this.boundHandleInput = this.handleInput.bind(this);
    this.boundHandleFocus = this.handleFocus.bind(this);
    this.boundHandleBlur = this.handleBlur.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleResultsMouseDown = this.handleResultsMouseDown.bind(this);
    this.boundHandleResultClick = this.handleResultClick.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundHandleBranchesSorted = this.handleBranchesSorted.bind(this);
    
    this.input.addEventListener('input', this.boundHandleInput);
    this.input.addEventListener('focus', this.boundHandleFocus);
    this.input.addEventListener('blur', this.boundHandleBlur);
    this.addEventListener('keydown', this.boundHandleKeyDown);
    this.resultsList?.addEventListener('mousedown', this.boundHandleResultsMouseDown);
    this.resultsList?.addEventListener('click', this.boundHandleResultClick);
    this.sectionContainer.addEventListener('click', this.boundHandleDocumentClick);
    this.branchList?.addEventListener('branches:sorted-by-location', this.boundHandleBranchesSorted);
  }

  handleBranchesSorted() {
    this.collectBranches();
  }

  collectBranches() {
    const branchItems = document.querySelectorAll('.boa-branch-item');
    if (!branchItems.length) {
      return;
    }

    this.allBranches = Array.from(branchItems).map(branch => {
      const city = (branch.getAttribute('data-branch-city') || '').trim();
      const title = (branch.querySelector('.boa-branch-item__title')?.textContent || '').trim();
      const addressElement = branch.querySelector('.boa-branch-item__detail-text');
      const address = addressElement?.textContent?.trim() || '';
      const verifoneId = branch.getAttribute('data-branch-verifone-id');
      
      return { city, title, address, verifoneId, element: branch };
    });
  }

  handleInput(event) {
    const term = event.target.value.trim();
    
    if (term === '') {
      this.showAllOptions();
      if (this.branchList) {
        this.branchList.filterBranches('');
      } else {
        const branchItems = Array.from(document.querySelectorAll('.boa-branch-item'));
        const emptyState = document.querySelector('.boa-branches-list__empty-state');
        const container = document.querySelector('[data-branches-container]');
        const branchListElement = document.querySelector('branch-list');
        branchListElement?.filterBranchesByTerm?.('', branchItems, emptyState, container);
      }
      return;
    }

    this.filterAutocompleteOptions(term);
  }

  handleFocus() {
    const term = this.input.value.trim();
    if (term) {
      this.filterAutocompleteOptions(term);
    } else {
      this.showAllOptions();
    }
  }

  handleBlur() {
    // Keep the dropdown open when focus moves into a result (keyboard Tab/Arrow).
    setTimeout(() => {
      if (this.contains(document.activeElement)) {
        return;
      }
      if (!this.mouseOverResults) {
        this.hideResults();
      }
    }, 200);
  }

  handleResultsMouseDown() {
    this.mouseOverResults = true;
    setTimeout(() => {
      this.mouseOverResults = false;
    }, 300);
  }

  handleResultClick(event) {
    const resultItem = event.target.closest('.boa-branch-search__result-item');
    if (!resultItem) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    this.selectAutocompleteOption(resultItem);
  }

  selectAutocompleteOption(resultItem) {
    if (!resultItem) {
      return;
    }

    const verifoneId = resultItem.getAttribute('data-branch-verifone-id');
    const title = resultItem.getAttribute('data-branch-title') || '';
    const city = resultItem.getAttribute('data-branch-city') || '';
    
    if (!this.input) {
      return;
    }

    this.input.value = city || title || '';
    
    if (verifoneId) {
      const branchItems = document.querySelectorAll('.boa-branch-item');
      for (const branch of branchItems) {
        const branchVerifoneId = branch.getAttribute('data-branch-verifone-id');
        branch.style.display = branchVerifoneId === verifoneId ? '' : 'none';
      }
    } else {
      const searchTerm = this.input.value.toLowerCase().trim();
      if (this.branchList) {
        this.branchList.filterBranches(searchTerm);
      } else {
        const branchItems = Array.from(document.querySelectorAll('.boa-branch-item'));
        const emptyState = document.querySelector('.boa-branches-list__empty-state');
        const container = document.querySelector('[data-branches-container]');
        const branchListElement = document.querySelector('branch-list');
        branchListElement?.filterBranchesByTerm?.(searchTerm, branchItems, emptyState, container);
      }
    }
    
    this.hideResults();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  handleKeyDown(event) {
    if (event.target !== this.input && !event.target.closest?.(".boa-branch-search__result-item")) {
      return;
    }

    const results = Array.from(
      this.resultsList?.querySelectorAll(".boa-branch-search__result-item") || []
    );
    const resultsOpen = !this.resultsContainer?.hasAttribute("hidden") && results.length > 0;
    const activeIndex = results.indexOf(document.activeElement);

    if (event.key === "ArrowDown" && resultsOpen) {
      event.preventDefault();
      const nextIndex = activeIndex < 0 ? 0 : Math.min(activeIndex + 1, results.length - 1);
      results[nextIndex].focus();
      return;
    }

    if (event.key === "ArrowUp" && resultsOpen) {
      event.preventDefault();
      if (activeIndex <= 0) {
        this.input?.focus();
      } else {
        results[activeIndex - 1].focus();
      }
      return;
    }

    if (event.key === "Enter") {
      if (activeIndex >= 0) {
        event.preventDefault();
        this.selectAutocompleteOption(results[activeIndex]);
        return;
      }

      if (event.target !== this.input) {
        return;
      }

      event.preventDefault();

      // Only select first result if results container is visible (not hidden)
      if (resultsOpen) {
        this.selectAutocompleteOption(results[0]);
        return;
      }

      const searchTerm = this.input.value.toLowerCase().trim();
      if (this.branchList) {
        this.branchList.filterBranches(searchTerm);
      } else {
        const branchItems = Array.from(document.querySelectorAll(".boa-branch-item"));
        const emptyState = document.querySelector(".boa-branches-list__empty-state");
        const container = document.querySelector("[data-branches-container]");
        const branchListElement = document.querySelector("branch-list");
        branchListElement?.filterBranchesByTerm?.(searchTerm, branchItems, emptyState, container);
      }
      this.hideResults();
      return;
    }

    if (event.key === "Escape") {
      this.hideResults();
      this.input.focus();
    }
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) {
      return;
    }
    
    this.hideResults();
  }

  disconnectedCallback() {
    if (this.input && this.boundHandleInput) {
      this.input.removeEventListener('input', this.boundHandleInput);
      this.input.removeEventListener('focus', this.boundHandleFocus);
      this.input.removeEventListener('blur', this.boundHandleBlur);
    }

    if (this.boundHandleKeyDown) {
      this.removeEventListener('keydown', this.boundHandleKeyDown);
    }
    
    if (this.resultsList && this.boundHandleResultsMouseDown) {
      this.resultsList.removeEventListener('mousedown', this.boundHandleResultsMouseDown);
      this.resultsList.removeEventListener('click', this.boundHandleResultClick);
    }
    
    if (this.sectionContainer && this.boundHandleDocumentClick) {
      this.sectionContainer.removeEventListener('click', this.boundHandleDocumentClick);
    }

    if (this.branchList && this.boundHandleBranchesSorted) {
      this.branchList.removeEventListener('branches:sorted-by-location', this.boundHandleBranchesSorted);
    }
  }

  filterAutocompleteOptions(term) {
    if (!this.resultsList || !this.resultsContainer) {
      return;
    }
    
    if (!this.allBranches.length) {
      this.collectBranches();
    }
    
    if (!this.allBranches.length) {
      this.hideResults();
      return;
    }
    
    const searchTerm = term.toLowerCase();
    const matchingBranches = [];
    
    for (const branch of this.allBranches) {
      const city = (branch.city || '').toLowerCase();
      const title = (branch.title || '').toLowerCase();
      
      if (city.includes(searchTerm) || title.includes(searchTerm)) {
        matchingBranches.push(branch);
      }
    }

    if (matchingBranches.length === 0) {
      this.hideResults();
      return;
    }
    
    this.resultsList.innerHTML = matchingBranches.map(branch => {
      return `
        <button type="button" class="boa-branch-search__result-item" data-branch-verifone-id="${branch.verifoneId}" data-branch-title="${this.escapeHtml(branch.title || '')}" data-branch-city="${this.escapeHtml(branch.city || '')}">
          <span class="boa-branch-search__result-title">${this.highlightText(branch.title || '', term)}</span>
          ${branch.city ? `<span class="boa-branch-search__result-city text--subdued">${branch.city}</span>` : ''}
        </button>
      `;
    }).join('');
    
    this.resultsContainer.removeAttribute('hidden');
  }

  showAllOptions() {
    if (!this.resultsList || !this.resultsContainer) {
      return;
    }
    
    if (!this.allBranches.length) {
      this.collectBranches();
    }
    
    if (!this.allBranches.length) {
      this.hideResults();
      return;
    }
    
    this.resultsList.innerHTML = this.allBranches.map(branch => {
      return `
        <button type="button" class="boa-branch-search__result-item" data-branch-verifone-id="${branch.verifoneId}" data-branch-title="${this.escapeHtml(branch.title || '')}" data-branch-city="${this.escapeHtml(branch.city || '')}">
          <span class="boa-branch-search__result-title">${branch.title || ''}</span>
          ${branch.city ? `<span class="boa-branch-search__result-city text--subdued">${branch.city}</span>` : ''}
        </button>
      `;
    }).join('');
    
    this.resultsContainer.removeAttribute('hidden');
  }

  highlightText(text, term) {
    if (!term) {
      return text;
    }
    
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  hideResults() {
    this.resultsContainer?.setAttribute('hidden', '');
  }
}

class BranchSelector {
  constructor() {
    this.init();
  }

  init() {
    const branchesSection = document.querySelector('.shopify-section--boa-main-branches');
    const branchDetailSection = document.querySelector('.shopify-section--boa-branch-detail');
    
    const handleClick = (event) => {
      const buyButton = event.target.closest('.boa-branch-item__buy-button, .boa-branch-detail__buy-button');
      if (buyButton?.hasAttribute('data-branch-verifone-id')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const verifoneId = buyButton.getAttribute('data-branch-verifone-id');
        const branchTitle = buyButton.getAttribute('data-branch-title') || '';
        const metaobjectId = buyButton.getAttribute('data-branch-metaobject-id') || '';
        const shouldRedirect = !buyButton.getAttribute('data-popup-link');
        if (verifoneId) {
          this.selectBranch(verifoneId, branchTitle, metaobjectId, shouldRedirect);
        }
        if (shouldRedirect) {
          return false;
        }
        this.showBranchDiscountPopup(buyButton);
        return false;
      }

      const actionsElement = event.target.closest('.boa-branch-item__actions');
      if (actionsElement && !event.target.closest('button, a')) {
        event.stopPropagation();
      }
    };

    if (branchesSection) {
      branchesSection.addEventListener('click', handleClick, true);
    }
    
    if (branchDetailSection) {
      branchDetailSection.addEventListener('click', handleClick, true);
    }
  }

  async selectBranch(verifoneId, branchTitle, metaobjectId = '', shouldRedirect = true) {
    try {
      const attributes = {
        branch_verifone_id: verifoneId,
        branch_title: branchTitle
      };
      
      if (metaobjectId) {
        attributes.branch_metaobject_id = metaobjectId;
      }
      
      const response = await fetch(`${window.themeVariables?.routes?.cartUrl || '/cart'}/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: attributes
        })
      });

      if (!response.ok) {
        this.showErrorFeedback();
        return;
      }

      const cart = await response.json();
      
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart }
      }));

      this.updateSelectedBranchUI(verifoneId);
      
      const homeUrl = window.themeVariables?.routes?.rootUrl || '/';
      if (!shouldRedirect) {
        return;
      }
      window.location.href = homeUrl;
    } catch {
      this.showErrorFeedback();
    }
  }
  showBranchDiscountPopup(buyButton) {
    const popupLink = buyButton.getAttribute('data-popup-link');
    const popup = document.getElementById('branch-discount-popup');
    if (!popup || !popupLink) {
      return
    }
      const popupImage = buyButton.getAttribute('data-popup-image');
      const popupEndDate = buyButton.getAttribute('data-popup-end-date');

      const imgWrapper = popup.querySelector('.branch-discount-popup__image');
      const imgEl = popup.querySelector('.branch-discount-popup__img');
      if (popupImage && imgWrapper && imgEl) {
        imgEl.src = popupImage;
        imgWrapper.removeAttribute('hidden');
      } else if (imgWrapper) {
        imgWrapper.setAttribute('hidden', '');
      }

      const endDateEl = popup.querySelector('.branch-discount-popup__end-date');
      const endDateValue = popup.querySelector('.branch-discount-popup__end-date-value');
      if (popupEndDate && endDateEl && endDateValue) {
        endDateValue.textContent = popupEndDate;
        endDateEl.removeAttribute('hidden');
      } else if (endDateEl) {
        endDateEl.setAttribute('hidden', '');
      }

      const linkEl = popup.querySelector('.branch-discount-popup__link');
      if (linkEl) {
        linkEl.href = popupLink;
      }

      popup.open = true;
  }
  updateSelectedBranchUI(verifoneId) {
    if (!verifoneId) {
      return;
    }

    const selectedItems = document.querySelectorAll('.boa-branch-item--selected');
    for (const item of selectedItems) {
      item.classList.remove('boa-branch-item--selected');
    }

    const newSelectedItems = document.querySelectorAll(`[data-branch-verifone-id="${verifoneId}"]`);
    for (const item of newSelectedItems) {
      item.classList.add('boa-branch-item--selected');
    }
  }

  showErrorFeedback() {
    document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: {
        status: 'error',
        error: ''
      }
    }));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.customElements.define('branch-list', BranchList);
    window.customElements.define('branch-search', BranchSearch);
    window.branchSelector = new BranchSelector();
  });
} else {
  window.customElements.define('branch-list', BranchList);
  window.customElements.define('branch-search', BranchSearch);
  window.branchSelector = new BranchSelector();
}

